
import { defineComponent, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import List from '@/common/components/UI/List/index.vue'

// module components
import RPPForm from '@/modules/RPPModule/components/RPPForm/index.vue'
import RPPOrdersListItem from '@/modules/RPPModule/components/RPPOrdersListItem/index.vue'
import RPPFilters from '@/modules/RPPModule/components/RPPFilters/index.vue'

// types
import { RPPOrdersListParamsT, RPPOrderT } from '@/core/types/rpp.types'

// realisations
import { rpp } from '@/core/realisations/rpp.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'
import { required } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'

// composable
import { useMaska } from '@/common/composable/useMaska'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        // 'the-button': TheButton,
        'failed-fetch': FailedFetch,
        list: List,
        'rpp-form': RPPForm,
        'rpp-orders-list-item': RPPOrdersListItem,
        'rpp-filters': RPPFilters,
    },
    setup() {
        const state = reactive({
            is_loading: false,
            // rpp
            carrier_id: null as number | null,
            driver_name: null as string | null,
            driver_phone: null as string | null,
            fromDate: null as string | null,
            toDate: null as string | null,
        })

        const data_state = reactive({
            rpp_orders: new DataState<RPPOrderT>({}),
        })

        const form_state = reactive({
            is_visible: false,
            is_loading: false,
            act_number: null as string | null,
            act_date: null as string | null,
            items: [] as number[],
        })

        const GetRPPValidation = {
            carrier_id: { required },
        }

        const state_form$ = useVuelidate(GetRPPValidation, state)

        const { unmaskNumbers } = useMaska()
        const notifications = useNotifications()

        const onSearch = (rpp: RPPOrdersListParamsT) => {
            state.carrier_id = rpp.carrier_id
            state.driver_name = rpp.driver_name
            state.driver_phone = rpp.driver_phone
            state.fromDate = rpp.fromDate
            state.toDate = rpp.toDate

            loadRPPOrdersList(1)
        }

        const loadRPPOrdersList = async (page: number) => {
            data_state.rpp_orders.pagination.current_page = page

            // filters
            const params = {
                page,
                carrier_id: state.carrier_id,
                driver_name: state.driver_name,
                fromDate: state.fromDate,
                toDate: state.toDate,
                driver_phone: state.driver_phone ? unmaskNumbers(state.driver_phone) : null,
            }

            try {
                state.is_loading = true
                const response = await rpp.getRPPOrdersList(params)

                data_state.rpp_orders.list = response.data
                data_state.rpp_orders.pagination.total_pages = response.meta.last_page

                data_state.rpp_orders.is_failed = false
                data_state.rpp_orders.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.rpp_orders.failure.catchError(error)

                if (!data_state.rpp_orders.failure.error_state.is_cancel) {
                    data_state.rpp_orders.is_failed = true
                }
            } finally {
                if (!data_state.rpp_orders.failure.error_state.is_cancel) {
                    data_state.rpp_orders.is_loading = false
                }

                state.is_loading = false
            }
        }

        const toggleRPPForm = () => {
            form_state.is_visible = !form_state.is_visible
        }

        const onOrderSelected = (order_id: number) => {
            if (form_state.items.includes(order_id)) {
                const index = form_state.items.indexOf(order_id)
                form_state.items.splice(index, 1)
            } else form_state.items.push(order_id)
        }

        const createRPP = async (act_number: string, act_date: string) => {
            try {
                form_state.is_loading = true

                await rpp.createRPP({
                    company_id: state.carrier_id,
                    act_number,
                    act_date,
                    items: form_state.items,
                })

                notifications.notify({ title: 'РПП создан', type: 'success' })
                toggleRPPForm()
            } catch (error) {
                notifications.notifyError({ error, title: 'Ошибка при создании РПП' })
            } finally {
                form_state.is_loading = false
            }
        }

        return {
            state,
            form_state,
            data_state,
            state_form$,
            // RPP
            loadRPPOrdersList,
            createRPP,
            toggleRPPForm,
            onOrderSelected,
            onSearch,
        }
    },
})
